import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface SpaceReservationListItem {
  reservation_id: number
  space_name: string
  reserve_date: string
  start_time: string | null
  end_time: string | null
  label: string | null
  applicant_name: string | null
  phone: string | null
  status: number
}

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: '0', label: '확인대기' },
  { value: '1', label: '확인완료' },
  { value: '2', label: '신청취소' },
]

const STATUS_STYLE: Record<number, { label: string; cls: string }> = {
  0: { label: '확인대기', cls: 'bg-[#fff8e1] text-[#e67e22]' },
  1: { label: '확인완료', cls: 'bg-[#e8f5e9] text-[#388e3c]' },
  2: { label: '신청취소', cls: 'bg-[#f5f5f5] text-[#9e9e9e]' },
}

const LIMIT = 20

export default function SpaceReservationTab() {
  const navigate = useNavigate()
  const [reservations, setReservations] = useState<SpaceReservationListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [skip, setSkip] = useState(0)
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null)
  const [excelLoading, setExcelLoading] = useState(false)

  const fetchReservations = useCallback(async (status: string, skipVal: number) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (status !== '') params.append('status', status)
      params.append('skip', String(skipVal))
      params.append('limit', String(LIMIT))
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/space/admin/reservations?${params.toString()}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setReservations(data.reservations)
      setTotal(data.total)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchReservations(statusFilter, skip)
  }, [statusFilter, skip, fetchReservations])

  const handleApprove = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (!window.confirm('예약을 확인(승인) 처리하시겠습니까?')) return
    setActionLoadingId(id)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/space/admin/reservations/${id}/approve`), { method: 'POST' })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchReservations(statusFilter, skip)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleCancel = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (!window.confirm('예약을 취소 처리하시겠습니까?')) return
    setActionLoadingId(id)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/space/admin/reservations/${id}/cancel`), { method: 'POST' })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchReservations(statusFilter, skip)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleExcelDownload = async () => {
    setExcelLoading(true)
    try {
      const params = new URLSearchParams()
      if (statusFilter !== '') params.append('status', statusFilter)
      const token = localStorage.getItem('access_token')
      const res = await fetch(getApiUrl(`/marubit/api/space/admin/reservations/excel?${params.toString()}`), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('다운로드 실패')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `space_reservations_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      alert('엑셀 다운로드에 실패했습니다.')
    } finally {
      setExcelLoading(false)
    }
  }

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })
  const formatTime = (t: string | null) => (t ? t.slice(0, 5) : '')

  const totalPages = Math.ceil(total / LIMIT)
  const currentPage = Math.floor(skip / LIMIT) + 1

  return (
    <div>
      {/* Filter + Excel */}
      <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex flex-wrap gap-3 items-end justify-between">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] text-[#5a3d32] font-medium">예약 상태</label>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setSkip(0) }}
            className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[160px] bg-white"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[13px] text-gray-400">
            총 <span className="text-[#887267] font-bold">{total}</span>건
          </span>
          <button
            onClick={handleExcelDownload}
            disabled={excelLoading}
            className="bg-[#388e3c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg cursor-pointer hover:bg-[#2e7d32] transition-colors disabled:opacity-50 border-none"
          >
            {excelLoading ? '다운로드 중...' : '📥 엑셀로 저장'}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
        {loading ? (
          <div className="min-h-[300px] flex items-center justify-center text-gray-300 text-[13px]">
            불러오는 중...
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">공간명</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]">예약 날짜</th>
                <th className="hidden md:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[140px]">예약 시간</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">신청자</th>
                <th className="hidden sm:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]">전화번호</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">상태</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]"></th>
              </tr>
            </thead>
            <tbody>
              {reservations.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-300 text-[13px]">
                    예약 내역이 없습니다.
                  </td>
                </tr>
              ) : reservations.map(r => {
                const st = STATUS_STYLE[r.status] ?? { label: String(r.status), cls: 'bg-gray-100 text-gray-500' }
                const isLoading = actionLoadingId === r.reservation_id
                const timeLabel = r.label ?? (r.start_time && r.end_time
                  ? `${formatTime(r.start_time)} ~ ${formatTime(r.end_time)}`
                  : '-')
                return (
                  <tr
                    key={r.reservation_id}
                    className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/space/reservations/${r.reservation_id}`)}
                  >
                    <td className="px-4 py-3 text-[13px] font-medium text-[#2a1f1a] whitespace-nowrap">{r.space_name}</td>
                    <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">{formatDate(r.reserve_date)}</td>
                    <td className="hidden md:table-cell px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">{timeLabel}</td>
                    <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">{r.applicant_name ?? '-'}</td>
                    <td className="hidden sm:table-cell px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">{r.phone ?? '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${st.cls}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      {r.status === 0 && (
                        <div className="flex gap-1.5">
                          <button
                            onClick={e => handleApprove(e, r.reservation_id)}
                            disabled={isLoading}
                            className="text-[12px] text-[#388e3c] hover:text-[#2e7d32] border border-[#c8e6c9] rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50"
                          >확인</button>
                          <button
                            onClick={e => handleCancel(e, r.reservation_id)}
                            disabled={isLoading}
                            className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50"
                          >취소</button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-1.5 mt-5">
          <button
            onClick={() => setSkip((currentPage - 2) * LIMIT)}
            disabled={currentPage === 1}
            className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
          >‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
            .reduce<(number | '...')[]>((acc, p, idx, arr) => {
              if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1)
                acc.push('...')
              acc.push(p)
              return acc
            }, [])
            .map((p, idx) =>
              p === '...'
                ? <span key={`e-${idx}`} className="w-8 h-8 flex items-center justify-center text-[12px] text-gray-300">…</span>
                : <button
                    key={p}
                    onClick={() => setSkip(((p as number) - 1) * LIMIT)}
                    className={`w-8 h-8 rounded-lg text-[12px] font-medium border transition-colors cursor-pointer ${
                      currentPage === p
                        ? 'bg-[#887267] text-white border-[#887267]'
                        : 'bg-white text-[#5a3d32] border-[#e8e0dc] hover:bg-[#f5f2f0]'
                    }`}
                  >{p}</button>
            )}
          <button
            onClick={() => setSkip(currentPage * LIMIT)}
            disabled={currentPage === totalPages}
            className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
          >›</button>
        </div>
      )}
    </div>
  )
}